/**
 * Inicio · lista blanca de un banner (promo-lista-blanca.js)
 *
 * Un banner del Inicio (y su popup) puede salir SOLO a una lista de usuarios
 * que se escribe en el backoffice (Popups → Audiencia → «Lista blanca»): un
 * correo, un @usuario de TikTok o un UID por línea (28-oct-2026, Javier).
 *
 * Los docs de `popups` los lee cualquier usuario con sesión, así que en el doc
 * NO van los correos ni los @: van sus huellas (SHA-256 de «tipo:valor», con
 * un prefijo, recortado a 32 cifras) en `audience.whitelist`. Cada app calcula
 * las huellas de su cuenta (su UID, su correo y sus @ de TikTok) y mira si
 * alguna está en la lista. La lista escrita tal cual solo la guarda el
 * backoffice en `popups_private/{id}` (reglas: solo ADMIN).
 *
 * Modelo ÚNICO: lo cargan tal cual la app (Inicio, antes de promos.js), el
 * backoffice (lib/promoWhitelist.js) y el Worker (contador de alcance).
 *   parseList(texto) → { entries: [{ raw, kind, key }], invalid: [...] }
 *   userKeys({ uid, email, tiktokUsername, … }) → claves de esa cuenta
 *   hashKey(clave) → Promise<huella>
 */
(function (root) {
  'use strict';

  var PREFIX = 'tc-promo-wl:';
  var HASH_HEX = 32;
  // La lista no tiene tope de entradas (hasta el 28-oct-2026 eran 500). El
  // único límite es el de Firestore, 1 MB por documento (unas 25.000-30.000
  // huellas): lo comprueba el backoffice antes de guardar.

  // Un correo (algo@algo.algo), un @usuario de TikTok o un UID de Firebase.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var TIKTOK_RE = /^[a-z0-9._]{2,24}$/;
  var UID_RE = /^[A-Za-z0-9]{20,40}$/;

  /**
   * Una línea → { raw, kind: 'email' | 'tiktok' | 'uid', key } o null.
   * Un texto sin @ que parece un UID (20 a 40 letras y cifras) es un UID; si
   * no, un usuario de TikTok. Correos y @ van en minúsculas; el UID, tal cual
   * (distingue mayúsculas).
   */
  function normalizeEntry(line) {
    var raw = String(line == null ? '' : line).trim().replace(/^[,;]+|[,;]+$/g, '');
    if (!raw) return null;
    if (EMAIL_RE.test(raw)) return { raw: raw, kind: 'email', key: 'email:' + raw.toLowerCase() };
    if (raw.charAt(0) === '@') {
      var handle = raw.slice(1).toLowerCase();
      return TIKTOK_RE.test(handle) ? { raw: raw, kind: 'tiktok', key: 'tiktok:' + handle } : null;
    }
    if (UID_RE.test(raw) && /[0-9]/.test(raw) && /[A-Za-z]/.test(raw)) return { raw: raw, kind: 'uid', key: 'uid:' + raw };
    var lower = raw.toLowerCase();
    return TIKTOK_RE.test(lower) ? { raw: raw, kind: 'tiktok', key: 'tiktok:' + lower } : null;
  }

  /**
   * Texto del backoffice (una entrada por línea; también vale separar con
   * comas o espacios) → { entries, invalid }. Sin repetidas y sin tope.
   */
  function parseList(text) {
    var parts = String(text == null ? '' : text).split(/[\n\r,;\t ]+/);
    var seen = Object.create(null);
    var seenInvalid = Object.create(null);
    var entries = [];
    var invalid = [];
    parts.forEach(function (part) {
      var trimmed = part.trim();
      if (!trimmed) return;
      var entry = normalizeEntry(trimmed);
      if (!entry) {
        if (!seenInvalid[trimmed]) { seenInvalid[trimmed] = true; invalid.push(trimmed); }
        return;
      }
      if (seen[entry.key]) return;
      seen[entry.key] = true;
      entries.push(entry);
    });
    return { entries: entries, invalid: invalid };
  }

  function addKey(out, kind, value) {
    var v = String(value == null ? '' : value).trim();
    if (!v) return;
    if (kind === 'tiktok') v = v.replace(/^@/, '').toLowerCase();
    else if (kind === 'email') v = v.toLowerCase();
    var key = kind + ':' + v;
    if (out.indexOf(key) < 0) out.push(key);
  }

  /**
   * Las claves con las que una cuenta puede estar en una lista: su UID, su
   * correo y sus @ de TikTok (el del directo que conecta, el vinculado en
   * Cuenta y el de la prueba de 7 días).
   */
  function userKeys(user) {
    var u = user && typeof user === 'object' ? user : {};
    var out = [];
    addKey(out, 'uid', u.uid || u.id);
    addKey(out, 'email', u.email);
    addKey(out, 'tiktok', u.tiktokUsername);
    addKey(out, 'tiktok', u.tiktokLinkedUsername);
    addKey(out, 'tiktok', u.trialTiktokUniqueId);
    return out;
  }

  function toHex(buffer) {
    var bytes = new Uint8Array(buffer);
    var hex = '';
    for (var i = 0; i < bytes.length; i++) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
    return hex;
  }

  /** Huella de una clave: SHA-256 de PREFIX + clave, las 32 primeras cifras. */
  function hashKey(key) {
    var subtle = root.crypto && root.crypto.subtle;
    if (!subtle) return Promise.reject(new Error('crypto.subtle no disponible'));
    var data = new TextEncoder().encode(PREFIX + String(key));
    return subtle.digest('SHA-256', data).then(function (buf) { return toHex(buf).slice(0, HASH_HEX); });
  }

  function hashKeys(keys) {
    return Promise.all((keys || []).map(hashKey));
  }

  /**
   * Lista de huellas guardada en un doc, limpia (solo hex de 32, sin
   * repetidas). Sin tope: con una lista larga se repasa una sola vez (nada de
   * indexOf por cada huella, que con miles se volvería lento en cada app).
   */
  function cleanHashes(list) {
    var out = [];
    var seen = Object.create(null);
    (Array.isArray(list) ? list : []).forEach(function (h) {
      var v = String(h == null ? '' : h).trim().toLowerCase();
      if (!seen[v] && /^[0-9a-f]{32}$/.test(v)) { seen[v] = true; out.push(v); }
    });
    return out;
  }

  var api = {
    PREFIX: PREFIX,
    normalizeEntry: normalizeEntry,
    parseList: parseList,
    userKeys: userKeys,
    hashKey: hashKey,
    hashKeys: hashKeys,
    cleanHashes: cleanHashes
  };

  root.TCPromoWhitelist = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
